import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Types
interface TagItem {
  id: string;
  key: string;
  value: string;
}

interface WorkPreset {
  id: string;
  name: string;
  description: string;
  // 언어 및 기본 정보
  languageSettings: {
    targetLanguages: string[];
    tags: { key: string; value: string }[];
    memo: string;
  };
  // 워크플로 설정
  workflow: {
    tasks: TaskNode[];
  };
  // 작업자 배정
  workerAssignment: {
    assignments: AssignmentCell[];
  };
  // 검색용 태그
  searchTags?: string[];
  // 레거시 필드들 (호환성을 위해 유지)
  workflowSummary?: string;
  workerCount?: number;
  tags?: string[];
}

interface TaskNode {
  id: string;
  type: TaskType;
  position: { x: number; y: number };
  duration: { value: number; unit: 'hour' | 'day' };
  order: number;
  label: string;
  icon: string;
}

interface AssignmentCell {
  languageCode: string;
  taskType: TaskType;
  assignee: {
    type: 'worker' | 'group' | null;
    id: string | null;
    name: string | null;
  };
  unitPrice: number | null;
  currency: string;
}

interface Job {
  id: string;
  language: string;
  tasks: TaskDetail[];
  totalCost: number;
  totalDuration: number;
}

interface TaskDetail {
  id: string;
  jobId: string;
  type: TaskType;
  assignee: {
    type: 'worker' | 'group';
    id: string;
    name: string;
  };
  unitPrice: number;
  currency: string;
  duration: { value: number; unit: 'hour' | 'day' };
  memo: string;
  catToolOptions: CatToolOptions;
}

interface CatToolOptions {
  enabled: boolean;
  tmEnabled: boolean;
  glossaryEnabled: boolean;
  mtEnabled: boolean;
}

type TaskType = 'transcription' | 'translation' | 'transcription_review' | 'translation_review' | 'translation_review2';

interface WorkSettingsStore {
  // 전체 플로우 상태
  currentStep: number;
  completedSteps: number[];
  
  // Step 0: 프리셋
  searchQuery: string;
  presets: WorkPreset[];
  filteredPresets: WorkPreset[];
  isLoadingPresets: boolean;
  selectedPreset: WorkPreset | null;
  isPresetApplied: boolean;
  
  // Step 1: 언어 설정
  targetLanguages: string[];
  tags: TagItem[];
  memo: string;
  tagKeyInput: string;
  tagValueInput: string;
  
  // Step 2: 워크플로
  taskNodes: TaskNode[];
  connections: any[]; // TODO: Connection 타입 정의
  isDragging: boolean;
  draggedItem: any | null;
  
  // Step 3: 작업자 배정
  assignmentMatrix: AssignmentCell[][];
  availableWorkers: any[]; // TODO: Worker 타입 정의
  availableGroups: any[]; // TODO: Group 타입 정의
  
  // Step 4: 최종 확인
  jobs: Job[];
  expandedJobs: Set<string>;
  modifiedTasks: Map<string, Partial<TaskDetail>>;
  
  // 액션들
  setCurrentStep: (step: number) => void;
  markStepCompleted: (step: number) => void;
  
  // 언어 설정 액션
  toggleLanguage: (language: string) => void;
  addTag: () => void;
  removeTag: (tagId: string) => void;
  setTagKeyInput: (value: string) => void;
  setTagValueInput: (value: string) => void;
  setMemo: (value: string) => void;
  
  // 프리셋 액션
  setSearchQuery: (query: string) => void;
  loadPresets: () => Promise<void>;
  filterPresets: () => void;
  selectPreset: (preset: WorkPreset) => void;
  applyPreset: (preset: WorkPreset) => Promise<void>;
  clearPreset: () => void;
  applyPresetLanguageSettings: (preset: WorkPreset) => void;
  
  // 워크플로 액션
  addTaskNode: (type: TaskType, afterNodeId?: string) => void;
  removeTaskNode: (nodeId: string) => void;
  updateTaskDuration: (nodeId: string, duration: { value: number; unit: 'hour' | 'day' }) => void;
  reorderTaskNodes: (sourceId: string, targetPosition: number) => void;
  
  // 작업자 배정 액션
  updateAssignmentCell: (language: string, taskType: TaskType, updates: Partial<AssignmentCell>) => void;
  loadWorkers: () => Promise<void>;
  loadGroups: () => Promise<void>;
  initializeAssignmentMatrix: () => void;
  
  // 최종 확인 액션
  toggleJobExpand: (jobId: string) => void;
  updateTaskPrice: (taskId: string, price: number) => void;
  updateTaskMemo: (taskId: string, memo: string) => void;
  updateCatToolOptions: (taskId: string, options: CatToolOptions) => void;
  generateJobs: () => void;
  
  // 유틸리티
  reset: () => void;
}

const initialState = {
  currentStep: 0,
  completedSteps: [],
  
  // Step 0: 프리셋
  searchQuery: '',
  presets: [],
  filteredPresets: [],
  isLoadingPresets: false,
  selectedPreset: null,
  isPresetApplied: false,
  
  // Step 1: 언어 설정
  targetLanguages: [],
  tags: [],
  memo: '',
  tagKeyInput: '',
  tagValueInput: '',
  
  // Step 2: 워크플로
  taskNodes: [],
  connections: [],
  isDragging: false,
  draggedItem: null,
  
  // Step 3: 작업자 배정
  assignmentMatrix: [],
  availableWorkers: [],
  availableGroups: [],
  
  // Step 4: 최종 확인
  jobs: [],
  expandedJobs: new Set<string>(),
  modifiedTasks: new Map(),
};

export const useWorkSettingsStore = create<WorkSettingsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      
      // 전체 플로우 액션
      setCurrentStep: (step) => set({ currentStep: step }),
      
      markStepCompleted: (step) => {
        const { completedSteps } = get();
        if (!completedSteps.includes(step)) {
          set({ completedSteps: [...completedSteps, step] });
        }
      },
      
      // Step 1: 언어 설정 액션
      toggleLanguage: (language) => {
        const { targetLanguages } = get();
        
        if (targetLanguages.includes(language)) {
          set({ targetLanguages: targetLanguages.filter(l => l !== language) });
        } else {
          set({ targetLanguages: [...targetLanguages, language] });
        }
      },
      
      addTag: () => {
        const { tagKeyInput, tagValueInput, tags } = get();
        
        if (!tagKeyInput.trim() || !tagValueInput.trim()) return;
        
        const newTag: TagItem = {
          id: `tag_${Date.now()}`,
          key: tagKeyInput.trim(),
          value: tagValueInput.trim()
        };
        
        set({
          tags: [...tags, newTag],
          tagKeyInput: '',
          tagValueInput: ''
        });
      },
      
      removeTag: (tagId) => {
        const { tags } = get();
        set({ tags: tags.filter(tag => tag.id !== tagId) });
      },
      
      setTagKeyInput: (value) => set({ tagKeyInput: value }),
      setTagValueInput: (value) => set({ tagValueInput: value }),
      setMemo: (value) => set({ memo: value }),
      
      // Step 0: 프리셋 액션
      setSearchQuery: (query) => set({ searchQuery: query }),
      
      loadPresets: async () => {
        set({ isLoadingPresets: true });
        
        try {
          // TODO: API 호출
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const mockPresets: WorkPreset[] = [
            {
              id: 'preset_001',
              name: '번역 표준 프리셋',
              description: '일반적인 번역 작업용 프리셋',
              languageSettings: {
                targetLanguages: ['en', 'ja'],
                tags: [
                  { key: '카테고리', value: '표준' },
                  { key: '유형', value: '번역' }
                ],
                memo: '표준 번역 품질 가이드라인 적용'
              },
              workflow: {
                tasks: [
                  { id: 'task_1', type: 'transcription', position: { x: 150, y: 200 }, duration: { value: 1, unit: 'day' }, order: 0, label: '전사', icon: '📝' },
                  { id: 'task_2', type: 'translation', position: { x: 400, y: 200 }, duration: { value: 2, unit: 'day' }, order: 1, label: '번역', icon: '🌐' },
                  { id: 'task_3', type: 'translation_review', position: { x: 650, y: 200 }, duration: { value: 12, unit: 'hour' }, order: 2, label: '번역검수', icon: '✅' }
                ]
              },
              workerAssignment: {
                assignments: []
              },
              searchTags: ['번역', '표준'],
              // 레거시 호환성
              workflowSummary: '전사 → 번역 → 검수',
              workerCount: 3,
              tags: ['번역', '표준']
            },
            {
              id: 'preset_002',
              name: '고품질 번역 프리셋',
              description: '높은 품질이 요구되는 번역 작업용',
              languageSettings: {
                targetLanguages: ['en', 'ja', 'zh'],
                tags: [
                  { key: '카테고리', value: '고품질' },
                  { key: '분야', value: '의료' }
                ],
                memo: '고품질 번역 작업 - 다중 검수 필수'
              },
              workflow: {
                tasks: [
                  { id: 'task_1', type: 'transcription', position: { x: 150, y: 200 }, duration: { value: 1, unit: 'day' }, order: 0, label: '전사', icon: '📝' },
                  { id: 'task_2', type: 'transcription_review', position: { x: 300, y: 200 }, duration: { value: 12, unit: 'hour' }, order: 1, label: '전사검수', icon: '🔍' },
                  { id: 'task_3', type: 'translation', position: { x: 450, y: 200 }, duration: { value: 2, unit: 'day' }, order: 2, label: '번역', icon: '🌐' },
                  { id: 'task_4', type: 'translation_review', position: { x: 600, y: 200 }, duration: { value: 12, unit: 'hour' }, order: 3, label: '번역검수1', icon: '✅' },
                  { id: 'task_5', type: 'translation_review2', position: { x: 750, y: 200 }, duration: { value: 8, unit: 'hour' }, order: 4, label: '번역검수2', icon: '✅' }
                ]
              },
              workerAssignment: {
                assignments: []
              },
              searchTags: ['번역', '고품질', '의료'],
              // 레거시 호환성
              workflowSummary: '전사 → 검수 → 번역 → 검수1 → 검수2',
              workerCount: 5,
              tags: ['번역', '고품질']
            },
            {
              id: 'preset_003',
              name: '단순 전사 프리셋',
              description: '전사만 필요한 작업용',
              languageSettings: {
                targetLanguages: ['ko'],
                tags: [
                  { key: '유형', value: '전사' },
                  { key: '난이도', value: '단순' }
                ],
                memo: '단순 전사 작업 - 음성 품질 양호'
              },
              workflow: {
                tasks: [
                  { id: 'task_1', type: 'transcription', position: { x: 150, y: 200 }, duration: { value: 1, unit: 'day' }, order: 0, label: '전사', icon: '📝' },
                  { id: 'task_2', type: 'transcription_review', position: { x: 400, y: 200 }, duration: { value: 8, unit: 'hour' }, order: 1, label: '전사검수', icon: '🔍' }
                ]
              },
              workerAssignment: {
                assignments: []
              },
              searchTags: ['전사', '단순'],
              // 레거시 호환성
              workflowSummary: '전사 → 전사검수',
              workerCount: 2,
              tags: ['전사', '단순']
            }
          ];
          
          set({ presets: mockPresets, filteredPresets: mockPresets });
        } finally {
          set({ isLoadingPresets: false });
        }
      },
      
      filterPresets: () => {
        const { searchQuery, presets } = get();
        
        if (!searchQuery.trim()) {
          set({ filteredPresets: presets });
          return;
        }
        
        const query = searchQuery.toLowerCase();
        const filtered = presets.filter(preset => 
          preset.name.toLowerCase().includes(query) ||
          preset.description.toLowerCase().includes(query) ||
          (preset.searchTags && preset.searchTags.some(tag => tag.toLowerCase().includes(query))) ||
          (preset.tags && preset.tags.some(tag => tag.toLowerCase().includes(query)))
        );
        
        set({ filteredPresets: filtered });
      },
      
      selectPreset: (preset) => set({ selectedPreset: preset }),
      
      applyPreset: async (preset) => {
        set({ 
          selectedPreset: preset,
          // 언어 설정 적용
          targetLanguages: [...preset.languageSettings.targetLanguages],
          tags: preset.languageSettings.tags.map((tag, index) => ({
            id: `tag_${Date.now()}_${index}`,
            key: tag.key,
            value: tag.value
          })),
          memo: preset.languageSettings.memo,
          // 워크플로 적용
          taskNodes: preset.workflow.tasks.map(task => ({
            ...task,
            id: `task_${Date.now()}_${task.order}` // 새로운 ID 생성
          })),
          // 프리셋 적용됨 표시
          isPresetApplied: true,
          // 워크플로와 작업자 배정 단계를 완료로 마킹
          completedSteps: [2, 3]
        });
      },
      
      clearPreset: () => {
        set({
          selectedPreset: null,
          targetLanguages: [],
          tags: [],
          memo: '',
          taskNodes: [],
          assignmentMatrix: [],
          isPresetApplied: false,
          completedSteps: []
        });
      },
      
      applyPresetLanguageSettings: (preset) => {
        const { languageSettings } = preset;
        set({
          targetLanguages: [...languageSettings.targetLanguages],
          tags: languageSettings.tags.map((tag, index) => ({
            id: `tag_${Date.now()}_${index}`,
            key: tag.key,
            value: tag.value
          })),
          memo: languageSettings.memo,
          isPresetApplied: true
        });
      },
      
      // Step 2: 워크플로 액션
      addTaskNode: (type, afterNodeId) => {
        const { taskNodes } = get();
        
        const newNode: TaskNode = {
          id: `task_${Date.now()}`,
          type,
          position: { x: 300, y: 200 },
          duration: { value: 1, unit: 'day' },
          order: taskNodes.length,
          label: type,
          icon: '📝'
        };
        
        if (afterNodeId) {
          const afterIndex = taskNodes.findIndex(n => n.id === afterNodeId);
          const updatedNodes = [...taskNodes];
          updatedNodes.splice(afterIndex + 1, 0, newNode);
          
          // 순서 재정렬
          updatedNodes.forEach((node, index) => {
            node.order = index;
          });
          
          set({ taskNodes: updatedNodes });
        } else {
          set({ taskNodes: [...taskNodes, newNode] });
        }
      },
      
      removeTaskNode: (nodeId) => {
        const { taskNodes } = get();
        set({ taskNodes: taskNodes.filter(node => node.id !== nodeId) });
      },
      
      updateTaskDuration: (nodeId, duration) => {
        const { taskNodes } = get();
        set({
          taskNodes: taskNodes.map(node =>
            node.id === nodeId ? { ...node, duration } : node
          )
        });
      },
      
      reorderTaskNodes: (sourceId, targetPosition) => {
        const { taskNodes } = get();
        const sourceIndex = taskNodes.findIndex(n => n.id === sourceId);
        
        if (sourceIndex === -1) return;
        
        const updatedNodes = [...taskNodes];
        const [removed] = updatedNodes.splice(sourceIndex, 1);
        updatedNodes.splice(targetPosition, 0, removed);
        
        // 순서 재정렬
        updatedNodes.forEach((node, index) => {
          node.order = index;
        });
        
        set({ taskNodes: updatedNodes });
      },
      
      // Step 3: 작업자 배정 액션
      updateAssignmentCell: (language, taskType, updates) => {
        const { assignmentMatrix } = get();
        const langIndex = get().targetLanguages.indexOf(language);
        const taskIndex = get().taskNodes.findIndex(n => n.type === taskType);
        
        if (langIndex === -1 || taskIndex === -1) return;
        
        const updatedMatrix = [...assignmentMatrix];
        updatedMatrix[langIndex][taskIndex] = {
          ...updatedMatrix[langIndex][taskIndex],
          ...updates
        };
        
        set({ assignmentMatrix: updatedMatrix });
      },
      
      loadWorkers: async () => {
        // TODO: API 호출
        const mockWorkers = [
          { id: 'worker_001', name: '김철수' },
          { id: 'worker_002', name: '이영희' },
          { id: 'worker_003', name: 'John Kim' }
        ];
        
        set({ availableWorkers: mockWorkers });
      },
      
      loadGroups: async () => {
        // TODO: API 호출
        const mockGroups = [
          { id: 'group_001', name: '번역팀A' },
          { id: 'group_002', name: '전사팀B' }
        ];
        
        set({ availableGroups: mockGroups });
      },
      
      initializeAssignmentMatrix: () => {
        const { targetLanguages, taskNodes } = get();
        
        const matrix: AssignmentCell[][] = targetLanguages.map(lang => 
          taskNodes.map(task => ({
            languageCode: lang,
            taskType: task.type,
            assignee: { type: null, id: null, name: null },
            unitPrice: null,
            currency: getCurrencyByLanguage(lang)
          }))
        );
        
        set({ assignmentMatrix: matrix });
      },
      
      // Step 4: 최종 확인 액션
      toggleJobExpand: (jobId) => {
        const { expandedJobs } = get();
        const newExpanded = new Set(expandedJobs);
        
        if (newExpanded.has(jobId)) {
          newExpanded.delete(jobId);
        } else {
          newExpanded.add(jobId);
        }
        
        set({ expandedJobs: newExpanded });
      },
      
      updateTaskPrice: (taskId, price) => {
        const { modifiedTasks } = get();
        const newModified = new Map(modifiedTasks);
        
        const existing = newModified.get(taskId) || {};
        newModified.set(taskId, { ...existing, unitPrice: price });
        
        set({ modifiedTasks: newModified });
      },
      
      updateTaskMemo: (taskId, memo) => {
        const { modifiedTasks } = get();
        const newModified = new Map(modifiedTasks);
        
        const existing = newModified.get(taskId) || {};
        newModified.set(taskId, { ...existing, memo });
        
        set({ modifiedTasks: newModified });
      },
      
      updateCatToolOptions: (taskId, options) => {
        const { modifiedTasks } = get();
        const newModified = new Map(modifiedTasks);
        
        const existing = newModified.get(taskId) || {};
        newModified.set(taskId, { ...existing, catToolOptions: options });
        
        set({ modifiedTasks: newModified });
      },
      
      generateJobs: () => {
        const { targetLanguages, taskNodes, assignmentMatrix, modifiedTasks } = get();
        
        const jobs: Job[] = [];
        
        targetLanguages.forEach((lang, langIndex) => {
          // 언어별로 5개의 Job 생성 (예시)
          for (let i = 0; i < 5; i++) {
            const jobId = `job_${lang}_${i + 1}`;
            const tasks: TaskDetail[] = taskNodes.map((taskNode, taskIndex) => {
              const assignment = assignmentMatrix[langIndex]?.[taskIndex];
              const taskId = `${jobId}_task_${taskIndex}`;
              const modifications = modifiedTasks.get(taskId) || {};
              
              return {
                id: taskId,
                jobId,
                type: taskNode.type,
                assignee: assignment?.assignee || { type: 'worker', id: '', name: '미배정' },
                unitPrice: modifications.unitPrice ?? assignment?.unitPrice ?? 0,
                currency: assignment?.currency || 'KRW',
                duration: taskNode.duration,
                memo: modifications.memo ?? '',
                catToolOptions: modifications.catToolOptions ?? {
                  enabled: false,
                  tmEnabled: false,
                  glossaryEnabled: false,
                  mtEnabled: false
                }
              };
            });
            
            const totalCost = tasks.reduce((sum, task) => sum + task.unitPrice, 0);
            
            jobs.push({
              id: jobId,
              language: lang,
              tasks,
              totalCost,
              totalDuration: 0 // TODO: 계산 로직
            });
          }
        });
        
        set({ jobs });
      },
      
      // 유틸리티
      reset: () => set(initialState)
    }),
    {
      name: 'work-settings-store'
    }
  )
);

// 헬퍼 함수
function getCurrencyByLanguage(languageCode: string): string {
  const currencyMap: Record<string, string> = {
    'ko': 'KRW',
    'en': 'USD',
    'ja': 'JPY',
    'zh': 'CNY',
    'es': 'EUR',
    'hi': 'INR'
  };
  
  return currencyMap[languageCode] || 'USD';
}